import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import React from 'react';
import { ISimpleSelectOption } from '../../Forms/ArrayFields/interfaces';
import SimpleSelectWithSearchHookForm from '../../Forms/Fields/SimpleSelectWithSearchHookForm';
import { handleCustomerOptionsBluecoin } from './BluecoinService';
import { FiltersBluecoin } from './types';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../../store/recoil/customers';
import { activeFilterBluecoinsAtom, isBluecoinDataLoadingAtom } from './BluecoinState';

interface FilterBluecoinsFormProps {
  closeFilterOptions: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  saveFilterWaypoint: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

interface FilterBluecoinsForm {
  customerId: number | string;
}

function FilterBluecoinsForm(props: FilterBluecoinsFormProps): React.JSX.Element {
  const { formatMessage: f } = useIntl();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const customers = useRecoilValue<ICustomer[]>(customersAtom);
  const [activeFilterBluecoin, setActiveFilterBluecoin] = useRecoilState<FiltersBluecoin | null>(
    activeFilterBluecoinsAtom
  );
  const setIsBluecoinDataLoading = useSetRecoilState<boolean>(isBluecoinDataLoadingAtom);

  const { handleSubmit, control, setValue, watch, clearErrors } = useForm<FilterBluecoinsForm>({
    mode: 'onChange',
    defaultValues: {
      customerId: activeFilterBluecoin?.customerIds ? activeFilterBluecoin?.customerIds[0] : '',
    },
    resolver: async (data: FilterBluecoinsForm) => {
      const formErrors = data.customerId
        ? {}
        : { customerId: { type: 'required', message: f({ id: 'form.required' }) } };

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleResetFilter = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): Promise<void> => {
    props.closeFilterOptions(event);
    setActiveFilterBluecoin(null);
    setIsBluecoinDataLoading(false);
  };

  const onSubmit = async (values: FilterBluecoinsForm): Promise<void> => {
    const filterList = {
      filters: {
        customerIds: [Number(values.customerId)],
      },
    };

    setActiveFilterBluecoin({ ...filterList.filters });

    props.saveFilterWaypoint();
    setIsBluecoinDataLoading(false);
  };

  const customerFormData: ISimpleSelectOption[] = handleCustomerOptionsBluecoin(
    customers,
    selectedCustomers
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="b-mc-waypoints">
        <div className="b-mc-waypoints-title">
          <a href="#">
            <i
              className="cmd-icon cmd-icon-back mr-1"
              onClick={props.closeFilterOptions}
              data-cy="bluecoin-form-back-icon"
            />
            {f({ id: 'bluecoins.form.filter.title' })}
          </a>
        </div>
        <div className="b-mc-waypoints-content">
          <Controller
            name="customerId"
            control={control}
            render={({ field }) => (
              <SimpleSelectWithSearchHookForm
                {...field}
                id="customerId"
                label={f({ id: 'waypoints.form.customer.title' })}
                labelClassName="col-3 form-label"
                introOption={f({ id: 'form.choose' })}
                options={customerFormData}
                rowClassName="row align-items-center"
                // columnClassName="col-9"
                searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                control={control}
                canRemoveValue={true}
                required={true}
                onChangeCallback={(value) => {
                  setValue('customerId', value);
                  clearErrors('customerId');
                }}
              />
            )}
          />
        </div>
        <div className="b-mc-waypoints-footer">
          <div className="row">
            <div className="col">
              <a href="" className="e-button" onClick={handleResetFilter}>
                {f({ id: 'waypoints.form.filter.clear' })}
              </a>
            </div>
            <div className="col">
              <button
                type="submit"
                className={`e-button e-button--gray ${
                  !watch('customerId') ? 'e-button--disabled' : ''
                }`}
              >
                {f({ id: 'waypoints.form.filter.apply' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default FilterBluecoinsForm;
